import React, { useEffect, useState } from 'react';
import { PagesConfig, usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, Filters, Option, PageType } from '@utils/pageConfig/Types';
import useDebouncedEffect from '@hooks/UseDebouncedEffect';
import RangePicker from '@components/range-picker/RangePicker';

interface DatePickerProps {
  updateFilterCallback: (pagesConfig: PagesConfig, pageType: PageType, toUpdate: Filters) => void;
  getDisplayValueDatePicker: (
    currentFilterStartDate: Filters,
    currentFilterEndDate: Filters,
  ) => { startDate: Date | undefined; endDate: Date | undefined };
  disabled?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({ updateFilterCallback, getDisplayValueDatePicker, disabled }) => {
  const START_DATE = 'Start date';
  const END_DATE = 'End date';
  const EARLIEST_DATE = new Date(1979, 12, 31);

  const startDateFilter = {
    dataType: START_DATE,
    feature: START_DATE,
    filterTitle: START_DATE,
    filterKey: FilterKeys.FIRST_ACTIVITY_START_DATE,
    options: [],
    disabled: false,
  };
  const endDateFilter = {
    dataType: END_DATE,
    feature: END_DATE,
    filterTitle: END_DATE,
    filterKey: FilterKeys.FIRST_ACTIVITY_END_DATE,
    options: [],
    disabled: false,
  };

  const pagesConfig = usePageConfig();

  const { startDate, endDate } = getDisplayValueDatePicker(startDateFilter, endDateFilter);

  const [from, setFrom] = useState(startDate);
  const [to, setTo] = useState(endDate);

  const updateSelectedFilters = (date: Date | undefined, filterTitle: string, filterKey: FilterKeys) => {
    let options: Option[] = [];
    if (date !== undefined) {
      // expected output: 2011-10-05T14:48:00.000Z -> 2011-10-05
      const dateString = date?.toISOString().slice(0, 10);
      options = [{ label: dateString, value: dateString }];
    }
    const toUpdate: Filters = {
      dataType: filterTitle,
      feature: filterTitle,
      filterTitle,
      filterKey,
      options,
      disabled: false,
    };
    updateFilterCallback(pagesConfig, PageType.RI_PRODUCT, toUpdate);
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      setFrom(undefined);
      setTo(undefined);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (from && to && startDate && !endDate) {
      const endOfMonthDate = to ? new Date(Date.UTC(to.getFullYear(), to.getMonth() + 1, 0)) : undefined;
      updateSelectedFilters(endOfMonthDate, END_DATE, FilterKeys.FIRST_ACTIVITY_END_DATE);
    }
  }, [startDate]);

  useDebouncedEffect(
    () => {
      if (from && to && startDate?.toDateString() !== from?.toDateString()) {
        const startOfMonthDate = from ? new Date(Date.UTC(from.getFullYear(), from.getMonth(), 1)) : undefined;
        updateSelectedFilters(startOfMonthDate, START_DATE, FilterKeys.FIRST_ACTIVITY_START_DATE);
      }

      //We need to check if 'from' is unselected, and we need to update the select filters
      if (!from) {
        updateSelectedFilters(undefined, END_DATE, FilterKeys.FIRST_ACTIVITY_START_DATE);
      }
    },
    [from, endDate],
    0,
  );

  useEffect(() => {
    if (from && to && startDate && endDate) {
      const endOfMonthDate = to ? new Date(Date.UTC(to.getFullYear(), to.getMonth() + 1, 0)) : undefined;
      updateSelectedFilters(endOfMonthDate, END_DATE, FilterKeys.FIRST_ACTIVITY_END_DATE);
    }

    //We need to check if 'to' is unselected, and we need to update the select filters
    if (!to) {
      updateSelectedFilters(undefined, START_DATE, FilterKeys.FIRST_ACTIVITY_END_DATE);
    }
  }, [to]);

  return (
    <div key={`${startDate}-${endDate}`} data-test-id={'date-group'}>
      <RangePicker
        disabled={disabled}
        from={startDate}
        to={endDate}
        minDate={EARLIEST_DATE}
        maxDate={new Date()}
        setFrom={setFrom}
        setTo={setTo}
      />
    </div>
  );
};

export default DatePicker;
